import { FEED_ITEM_KINDS, FeedItemSchema, MessageRequestSchema, type FeedItem, type FeedItemKind } from "@scrile/contracts";

/**
 * Лента переписки на клиенте: разбор ответа GET /feed, курсор и склейка страниц.
 *
 * Сервер отдаёт видимое после `after` по seq, до 100 за раз; благодаря монотонному
 * deliver_at это всегда непрерывный кусок, так что следующий after — наибольший seq
 * ответа. Элемент, который не прошёл схему (сервер новее клиента), выбрасывается, но seq
 * его учитывается: иначе клиент переспрашивал бы его вечно и не увидел бы ничего после.
 */

/** Потолок страницы ленты на сервере: полная страница — значит, есть ещё, спрашиваем без ожидания. */
export const FEED_PAGE_SIZE = 100;

export interface FeedPage {
  items: FeedItem[];
  /** Сколько элементов было в ответе, включая выброшенные. */
  count: number;
  /** Наибольший seq в ответе (и у выброшенных); null — пустой ответ. */
  maxSeq: number | null;
  /** Через сколько мс станет виден следующий элемент (x-feed-next-in); null — не ждём или сервер не сказал. */
  nextInMs: number | null;
  nextKind: FeedItemKind | null;
}

export interface ParsedItems {
  items: FeedItem[];
  count: number;
  maxSeq: number | null;
  dropped: number;
}

function rawSeq(entry: unknown): number | null {
  if (typeof entry !== "object" || entry === null || !("seq" in entry)) return null;
  const seq = (entry as { seq: unknown }).seq;
  return typeof seq === "number" && Number.isSafeInteger(seq) && seq >= 0 ? seq : null;
}

/** Тело ответа ленты → элементы. Не массив — null: это не ответ ленты, а ошибка. */
export function parseFeedItems(raw: unknown): ParsedItems | null {
  if (!Array.isArray(raw)) return null;
  const items: FeedItem[] = [];
  let maxSeq: number | null = null;
  let dropped = 0;
  for (const entry of raw) {
    const seq = rawSeq(entry);
    if (seq !== null) maxSeq = maxSeq === null ? seq : Math.max(maxSeq, seq);
    const parsed = FeedItemSchema.safeParse(entry);
    if (parsed.success && seq !== null && Number.isFinite(Date.parse(parsed.data.at))) items.push(parsed.data);
    else dropped += 1;
  }
  return { items, count: raw.length, maxSeq, dropped };
}

/** Заголовки подсказки «печатает»: кривые значения — как будто их нет. */
export function parseNextHint(nextIn: string | null, nextKind: string | null): Pick<FeedPage, "nextInMs" | "nextKind"> {
  const trimmed = nextIn?.trim() ?? "";
  const ms = /^\d{1,10}$/.test(trimmed) ? Number(trimmed) : null;
  const kind = (FEED_ITEM_KINDS as readonly string[]).includes(nextKind?.trim() ?? "")
    ? (nextKind?.trim() as FeedItemKind)
    : null;
  return { nextInMs: ms, nextKind: ms === null ? null : kind };
}

/** Следующий after: курсор не уходит назад, даже если сервер вдруг ответил старым. */
export function advanceCursor(cursor: number, page: Pick<FeedPage, "maxSeq">): number {
  return page.maxSeq === null ? cursor : Math.max(cursor, page.maxSeq);
}

/**
 * Склейка ленты: элементы неизменяемы, поэтому повтор seq (переспросили тот же after,
 * гонка двух запросов после возврата во вкладку) обновляет URL медиа. Порядок — по seq.
 */
export function mergeFeed(current: readonly FeedItem[], incoming: readonly FeedItem[]): FeedItem[] {
  if (incoming.length === 0) return current as FeedItem[];
  const last = current.at(-1)?.seq ?? -1;
  // Обычный случай — всё новое и по порядку: без сортировки и множества
  let ordered = true;
  let prev = last;
  for (const item of incoming) {
    if (item.seq <= prev) {
      ordered = false;
      break;
    }
    prev = item.seq;
  }
  if (ordered) return [...current, ...incoming];
  const bySeq = new Map<number, FeedItem>();
  for (const item of current) bySeq.set(item.seq, item);
  for (const item of incoming) {
    const previous = bySeq.get(item.seq);
    // Текст неизменяем; URL подписан заново каждым чтением и должен освежаться.
    if (!previous || item.kind === "voice" || item.kind === "photo" || item.kind === "video") bySeq.set(item.seq, item);
  }
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

/** Наибольший seq ленты — курсор, с которого продолжать. */
export function lastSeq(items: readonly FeedItem[]): number {
  let max = 0;
  for (const item of items) if (item.seq > max) max = item.seq;
  return max;
}

/**
 * Лимит длины сообщения — из схемы контракта, а не копией числа: поменяется там —
 * поменяется и счётчик у поля ввода.
 */
export function messageMaxLength(): number {
  for (const option of MessageRequestSchema.options) {
    const text = option.shape as Record<string, unknown>;
    const field = text["text"] as { maxLength?: number | null } | undefined;
    if (field && typeof field.maxLength === "number") return field.maxLength;
  }
  return 2000;
}

/** Элемент от героя, на который стоит «печатать» и который снимает ожидание ответа. */
export function isHeroMessage(item: FeedItem): boolean {
  if (item.from !== "hero") return false;
  switch (item.kind) {
    case "text":
    case "voice":
    case "photo":
    case "video":
    case "hero_card":
      return true;
    case "system":
      return item.body.type === "reminder" || item.body.type === "busy";
    default:
      return false;
  }
}
